import { ButtonColours } from "../../../../containers/app/themes";
import { IFilterStatus, IProps, RemoveFilterButton } from ".";
import { TTypedTFunction, useTranslation } from "@lib/useTypedTranslation";
import { FunctionComponent, useContext } from "react";
import { FilterContext } from "@components/data-table/filters/context";

type FilterBoxProps = Pick<IProps, 'onFilterRemoved'> & {
  id: string,
  filters: IFilterStatus[]
};

const getGroupDisplayName = (group: string, count: number, t: TTypedTFunction<'filterStatus' | 'batteryEssentials'>): string => {
  switch (group) {
  case 'manufacturers':
    return t('MANUFACTURER', { count, ns: 'filterStatus' });
  case 'models':
    return t('MODEL', { count, ns: 'filterStatus' });
  case 'batteryPartNumbers':
    return t('BATTERY_PART_NUMBER', { count, ns: 'batteryEssentials' });
  case 'groups':
    return t('GROUP', { count, ns: 'filterStatus' });
  case 'homeLocations':
    return t('HOME_LOCATION', { count, ns: 'filterStatus' });
  case 'platforms':
    return t('PLATFORM', { count, ns: 'filterStatus' });
  case 'alertStatus':
    return t('ALERT_STATUS', { count, ns: 'filterStatus' });
  case 'status':
    return t('DEVICE_STATUS', { count, ns: 'filterStatus' });
  default:
    throw new Error('Unknown filter group: ' + group);
  }
};

const FilterBox: FunctionComponent<FilterBoxProps> = ({ id, filters, onFilterRemoved }) => {
  const { t } = useTranslation(['filterStatus', 'batteryEssentials']);
  const filterContext = useContext(FilterContext);

  const onGroupRemove = onFilterRemoved && (() => {
    onFilterRemoved(filters[0].id, filters[0]);
  });

  const displayName = filterContext?.getFilterGroupDisplayName
    ? filterContext.getFilterGroupDisplayName(id, filters.length)
    : getGroupDisplayName(id, filters.length, t);

  return <div key={`filter_box_${id}`} data-cy={`filter-id--${id}`} data-id={`filter-id--${id}`} className={`filter-box`}>
    {onGroupRemove &&
      <RemoveFilterButton
        ariaLabel={`remove ${id} filter button`}
        dataId={`${id}-filter_close_button`}
        colour={ButtonColours.grey}
        iconStyle={'fas fa-times'}
        onClick={onGroupRemove}
      />}
    <span>
      {displayName}
    </span>
  </div>;
};

export const NewFilterStatus = (props: IProps) => {
  const { t } = useTranslation('translation');

  const groups = props.selectedFilters.reduce<Record<string, IFilterStatus[]>>((acc, filter) => {
    return {
      ...acc,
      [filter.group]: [...(acc[filter.group] || []), filter]
    };
  }, {});

  const filterBoxes = Object.keys(groups).map((group) => {
    return <FilterBox
      key={`filter_box_${group}`}
      id={group}
      filters={groups[group]}
      onFilterRemoved={props.onFilterRemoved}
    />;
  });

  return (
    <div className='filter-status-block new-filter-status-block'>
      <div className="filter-row">
        {filterBoxes}

        {props.selectedFilters.length > 0 &&
          <div className="filter-controls">
            {props.onClearFilters &&
              <span className="navigate clear" onClick={props.onClearFilters}>{t('CLEAR_ALL')}</span>}
          </div>}
      </div>
    </div>
  );
};
